/**
 * The designed non-content states, once. OWNER: web-frontend-engineer.
 *
 * `[T-74]` says an error has four parts — what happened, what it means, what to
 * do, who to write to — so there is one component that renders four parts and
 * no component that renders "Oops". `ST-403` and `ST-404` are the same
 * component in the same voice, and 403's copy is IDENTICAL to "does not exist"
 * because distinguishing them leaks whether an order or a client exists.
 */

import { t, type CopyKey } from '@pt/copy';
import { useHouse } from '../app/SessionProvider.tsx';
import { Button, ErrorStatement, Skeleton, Statement } from './primitives.tsx';

export function FetchError({
  whatKey,
  onRetry,
}: {
  whatKey?: CopyKey;
  onRetry?: () => void;
}) {
  const { contact } = useHouse();
  return (
    <div>
      <ErrorStatement
        what={whatKey === undefined ? t('error.fetch.what') : t(whatKey)}
        means={t('error.fetch.means')}
        todo={t('error.fetch.do')}
        who={t('error.fetch.who', { contactName: contact?.displayName ?? '' })}
      />
      {onRetry === undefined ? null : (
        <div className="pt-actions">
          <Button onClick={onRetry}>{t('global.action.retry')}</Button>
        </div>
      )}
    </div>
  );
}

export function SaveError() {
  const { contact } = useHouse();
  return (
    <ErrorStatement
      what={t('error.save.what')}
      means={t('error.save.means')}
      todo={t('error.save.do')}
      who={t('error.save.who', { contactName: contact?.displayName ?? '' })}
    />
  );
}

export function ServerError({ onRetry }: { onRetry?: () => void }) {
  const { contact } = useHouse();
  return (
    <div>
      <ErrorStatement
        what={t('error.server.what')}
        means={t('error.server.means')}
        todo={t('error.server.do')}
        who={t('error.server.who', { contactName: contact?.displayName ?? '' })}
        assertive
      />
      {onRetry === undefined ? null : (
        <div className="pt-actions">
          <Button onClick={onRetry}>{t('global.action.retry')}</Button>
        </div>
      )}
    </div>
  );
}

export function OfflineStatement() {
  return <Statement>{t('offline.statement')}</Statement>;
}

export function LoadingRegion({
  rows,
  variant,
  slow,
  label,
}: {
  rows: number;
  variant: 'ledger' | 'table' | 'tile' | 'block';
  slow: boolean;
  label: string;
}) {
  return <Skeleton rows={rows} variant={variant} slow={slow} slowText={t('error.fetch.do')} label={label} />;
}

/** ST-403 and ST-404 — one component, one voice. Never a machine code. */
export function Refused({ kind }: { kind: 403 | 404 }) {
  return (
    <div>
      <Statement>{kind === 403 ? t('e403.body') : t('e404.body')}</Statement>
    </div>
  );
}
